/**
 * Customer Welcome Email Service
 * Sends welcome email to new customers with:
 * - Welcome message
 * - List of benefits
 * - App Store and Play Store links
 * - QR codes for app downloads
 */

import { getDownloadLinks } from '../config/appDistributionConfig';
import { WIDGET_BOOKING_API } from '../utils/widgetEmbed';

interface Customer {
  id: string;
  email: string;
  name: string;
}

interface CustomerWelcomeEmailData {
  customer: Customer;
  appStoreUrl: string;
  playStoreUrl: string;
  webAppUrl: string;
}

class CustomerWelcomeEmailService {
  // Same production API host every other transactional email service uses
  // (bookapt-payment-api on Render) — this previously pointed at a stale
  // Heroku URL that no longer exists, so the email silently failed to send.
  private readonly API_BASE_URL = __DEV__
    ? 'http://localhost:3000/api'
    : WIDGET_BOOKING_API;

  /**
   * Send welcome email to new customer
   */
  async sendWelcomeEmail(customer: Customer): Promise<boolean> {
    try {
      console.log('📧 Sending customer welcome email to:', customer.email);

      // Real app store / web app links (single source of truth, shared with
      // the guest booking confirmation email) instead of hardcoded
      // placeholder URLs (the previous App Store link used a fake app id).
      const links = getDownloadLinks();

      const emailData: CustomerWelcomeEmailData = {
        customer,
        appStoreUrl: links.iosLink,
        playStoreUrl: links.androidLink,
        webAppUrl: links.webLink,
      };

      // Send email via backend API
      const response = await fetch(`${this.API_BASE_URL}/email/send-customer-welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Customer welcome email sent successfully');
        return true;
      } else {
        console.error('❌ Failed to send customer welcome email:', result.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending customer welcome email:', error);
      return false;
    }
  }
}

export const customerWelcomeEmailService = new CustomerWelcomeEmailService();
export default customerWelcomeEmailService;
